import {useInstallerPhotos,useJobInstalls} from "../hooks/useReads";
// Draft notes for each job. The backbone holds completion truth
// (portal.install_report). Isolated by authenticated account, then job ref. The old unowned storage
// key is preserved but never adopted by a signed-in account.
// Draft notes remain local. Photo inventory, acceptance and day completion come
// from the server; old browser flags and photo URLs are not evidence.
import { useSyncExternalStore } from "react";
import { captureSession } from "../lib/session-scope";

export interface Photo { path: string; url: string; name: string; at: string; label?: string; receipt?:string }
export interface JobReport {
  set_out_at: string | null;
  photos: Photo[];
  days_done: Record<string, string>; // day index → ISO sent
  notes: string;
  photos_error?:boolean;
  photos_loading?:boolean;
  offer?: "accepted" | "declined" | "asked";
}

let owner: string | null = null;
const storageKey=()=>owner?`aiw.installer.reports.user.${encodeURIComponent(owner)}`:null;
const EMPTY: JobReport = { set_out_at: null, photos: [], days_done: {}, notes: "" };

let state: Record<string, JobReport> = {};
const listeners = new Set<() => void>();

function load(): Record<string, JobReport> {
  try {
    const key=storageKey();
    const raw = key?localStorage.getItem(key):null;
    if (raw) return JSON.parse(raw) as Record<string, JobReport>;
  } catch { /* private mode or bad JSON */ }
  return {};
}
function save() {
  try { const key=storageKey();if(key)localStorage.setItem(key, JSON.stringify(state)); } catch { /* private mode */ }
  for (const l of listeners) l();
}

export function setReportOwner(next:string|null){owner=next;state=next?load():{};for(const l of listeners)l();}
/** Capture the session at render: late uploads/reports cannot write another account's drafts. */
export function useReportActions(){
  useSyncExternalStore(subscribe,snapshot,snapshot);
  const assertCurrent=captureSession();
  return {assertCurrent,
    updateReport:(ref:string,patch:Partial<JobReport>|((r:JobReport)=>Partial<JobReport>))=>{try{assertCurrent();}catch{return;}updateReport(ref,patch);},
    addPhoto:(ref:string,photo:Photo)=>{try{assertCurrent();}catch{return;}addPhoto(ref,photo);}
  };
}

export function reportFor(ref: string): JobReport {
  return state[ref] ?? EMPTY;
}
function updateReport(ref: string, patch: Partial<JobReport> | ((r: JobReport) => Partial<JobReport>)) {
  const cur = reportFor(ref);
  const p = typeof patch === "function" ? patch(cur) : patch;
  state = { ...state, [ref]: { ...cur, ...p } };
  save();
}
function addPhoto(ref: string, photo: Photo) {
  updateReport(ref, (r) => ({ photos: [...r.photos, photo] }));
}

function subscribe(l: () => void) { listeners.add(l); return () => { listeners.delete(l); }; }
const snapshot = () => state;

export function useReport(ref: string | null | undefined): JobReport {
  const s = useSyncExternalStore(subscribe, snapshot, snapshot);
  const installs=useJobInstalls(ref),installation=installs.data?.[0]?.id;
  const photos=useInstallerPhotos(ref??"",installation);
  const local=ref?(s[ref]??EMPTY):EMPTY;
  return {...local,photos_error:installs.isError||photos.isError,photos_loading:installs.isLoading||photos.isLoading,photos:(photos.data??[]).map(p=>({...p,label:p.label??undefined,url:`/api/installer-photos/${encodeURIComponent(ref!)}/${encodeURIComponent(installation!)}/${p.path.split('/').at(-1)}`}))};
}
export function useReports(): Record<string, JobReport> {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}
